import ModalContext from 'contexts/ModalContext'
import { useCallback, useContext } from 'react'
import * as React from 'react'
import type { ModalFooterOnClickHandler } from 'LuxKit/Modal/ModalFooter'
import LuxPlusLabel from 'LuxKit/Label/LuxPlusLabel'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { connect } from 'react-redux'
import LuxPlusBenefitsModal from './modals/LuxPlusBenefitsModal'
import LuxPlusLabelContext from 'contexts/LuxPlusLabelContext'

interface MappedProps {
  canViewLuxPlusBenefits: boolean
}

interface Props extends MappedProps {
  offer?: App.Offer | App.OfferSummary | App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary | App.CruiseOffer
  saveAmount?: number
  inclusionDescription?: string
  type: App.LuxPlusLabelModalType
  hideSaveAmountPlus?: boolean
  /** Presentation only; the existing membership and modal behaviour is retained. */
  hideInfoIcon?: boolean
  inclusions?: Array<App.PackageInclusion> | Array<App.OrderLuxPlusInclusion>
  tileInclusions?: Array<App.OfferInclusion>
  modalTitle?: string
  modalSubTitle?: React.ReactNode
  hideUpsellModal?: boolean
  isModalPrimaryActionHidden?: boolean
  primaryActionText?: string
  onPrimaryActionClick?: ModalFooterOnClickHandler
  secondaryActionText?: string | React.ReactElement
  onSecondaryActionClick?: ModalFooterOnClickHandler
  hideSpecialLabel?: boolean
  /** Benefit types to list first in the benefits modal (others follow in original order). */
  priorityBenefitTypes?: Array<App.LuxPlusBenefitType>
}

function LuxPlusLabelWithModal(props: Props) {
  const {
    offer,
    canViewLuxPlusBenefits,
    saveAmount,
    inclusionDescription,
    type,
    hideSaveAmountPlus = false,
    hideInfoIcon,
    inclusions,
    tileInclusions,
    modalTitle,
    modalSubTitle,
    hideUpsellModal,
    isModalPrimaryActionHidden,
    primaryActionText,
    onPrimaryActionClick,
    secondaryActionText,
    onSecondaryActionClick,
    hideSpecialLabel,
    priorityBenefitTypes,
  } = props

  const showModal = useContext(ModalContext)
  const providerPage = useContext(LuxPlusLabelContext)

  const toggleModal = useCallback(() => {
    showModal(<LuxPlusBenefitsModal
      type={type}
      saveAmount={saveAmount}
      offer={offer}
      hideSaveAmountPlus={hideSaveAmountPlus}
      providerPage={providerPage}
      inclusions={inclusions}
      tileInclusions={tileInclusions}
      title={modalTitle}
      subTitle={modalSubTitle}
      primaryActionText={primaryActionText}
      onPrimaryActionClick={onPrimaryActionClick}
      secondaryActionText={secondaryActionText}
      onSecondaryActionClick={onSecondaryActionClick}
      isPrimaryActionHidden={isModalPrimaryActionHidden}
      hideSpecialLabel={hideSpecialLabel}
      priorityBenefitTypes={priorityBenefitTypes}
    />)
  }, [
    hideSaveAmountPlus,
    hideSpecialLabel,
    inclusions,
    tileInclusions,
    modalSubTitle,
    modalTitle,
    offer,
    providerPage,
    saveAmount,
    showModal,
    type,
    isModalPrimaryActionHidden,
    onPrimaryActionClick,
    onSecondaryActionClick,
    primaryActionText,
    priorityBenefitTypes,
    secondaryActionText,
  ])

  return (
    <LuxPlusLabel
      saveAmount={saveAmount}
      inclusionDescription={inclusionDescription}
      type={type}
      onClick={(canViewLuxPlusBenefits || hideUpsellModal) ? undefined : toggleModal}
      hideSaveAmountPlus={hideSaveAmountPlus}
      hideInfoIcon={hideInfoIcon}
    />
  )
}

const mapStateToProps = (state: App.State): MappedProps => ({
  canViewLuxPlusBenefits: getCanViewLuxPlusBenefits(state),
})

export default connect(mapStateToProps)(LuxPlusLabelWithModal)
